/** Singleton app state: identity, the active group, definitions.
 *
 * Extends the shared WorkshopSession from @starling-cloud/sturnia-workshop
 * (identity, group lifecycle, participants, polling — the workshop
 * contract) with scapescene's own state: definitions, frames, film.
 * Screens and components subscribe with store.onChange(fn) and read
 * store.group / store.definitions synchronously. All API mutations go
 * through here so every screen sees the same cache.
 */

import { WorkshopSession } from "@starling-cloud/sturnia-workshop";

import * as api from "./data/api";
import { client } from "./data/client";
import {
  Definitions,
  fetchDefinitions,
} from "./data/definitions";
import { identityStore } from "./data/identity";
import type {
  ApiFrame,
  ApiGroup,
  FramePatch,
  GroupPatch,
} from "./data/types";
import { router } from "./control/router";

const POLL_MS = 5000;

export class Store extends WorkshopSession<ApiGroup> {
  static #instance: Store | null = null;

  static use(): Store {
    if (!Store.#instance) Store.#instance = new Store();
    return Store.#instance;
  }

  definitions: Definitions | null = null;
  videoEnabled = false;

  private constructor() {
    super({
      client,
      identity: identityStore,
      groups: api.groups,
      groupLabel: (group) => group.name || group.projectTitle,
    });
    // Navigation is the app's job: the session only announces.
    this.addEventListener("group-entered", () => router.navigate("/group"));
    this.addEventListener("left", () => router.navigate("/start"));
  }

  async init(): Promise<void> {
    try {
      this.definitions = await fetchDefinitions();
    } catch {
      this.definitions = null;
    }
    try {
      this.videoEnabled = (await api.health()).videoEnabled;
    } catch {
      this.videoEnabled = false;
    }
    if (this.membership) {
      await this.refreshGroup().catch(() => undefined);
    }
    this.emitChange();
  }

  /** Poll the group at the app's cadence. */
  override startPolling(intervalMs: number = POLL_MS): void {
    super.startPolling(intervalMs);
  }

  // -- reads ------------------------------------------------------------

  frame(position: number): ApiFrame | null {
    return this.group?.frames.find((f) => f.position === position) ?? null;
  }

  #mergeFrame(frame: ApiFrame): void {
    if (this.group) {
      this.group = {
        ...this.group,
        frames: this.group.frames.map((f) =>
          f.position === frame.position ? frame : f,
        ),
      };
    }
    this.emitChange();
  }

  // -- mutations --------------------------------------------------------

  /** Autosave group fields (title, name, picks, change sentence). */
  override patchGroup(patch: GroupPatch): Promise<ApiGroup> {
    return super.patchGroup(patch as Partial<ApiGroup>);
  }

  async patchFrame(position: number, patch: FramePatch): Promise<void> {
    const active = this.membership;
    if (!active) return;
    const frame = await api.patchFrame(active.code, position, patch);
    this.#mergeFrame(frame);
  }

  async uploadSketch(position: number, file: File): Promise<void> {
    const active = this.membership;
    if (!active) return;
    const frame = await api.uploadSketch(active.code, position, file);
    this.#mergeFrame(frame);
  }

  async renderFrame(position: number): Promise<void> {
    const active = this.membership;
    if (!active) return;
    const result = await api.renderFrame(active.code, position);
    this.#mergeFrame(result.frame);
    this.startPolling();
  }

  async renderStill(position: number): Promise<void> {
    const active = this.membership;
    if (!active) return;
    const result = await api.renderStill(active.code, position);
    this.#mergeFrame(result.frame);
    this.startPolling();
  }

  async renderClip(position: number): Promise<void> {
    const active = this.membership;
    if (!active) return;
    const result = await api.renderClip(active.code, position);
    this.#mergeFrame(result.frame);
    this.startPolling();
  }

  async renderFilm(): Promise<void> {
    const active = this.membership;
    if (!active) return;
    const result = await api.renderFilm(active.code);
    this.group = result.group;
    this.emitChange();
    this.startPolling();
  }
}

export const store = Store.use();
